import type { ReactElement } from 'react';
import { memo, useCallback, useEffect, useRef } from 'react';

import { useEventListener } from '../../../../hooks';
import { classNames, useComponentDidMount } from '../../../../utils';
import styles from '../STabs.module.css';
import type { BaseSTabProps, STabMeasurements } from '../types';
import { STab } from './STab';

export interface STabMeasurerProps {
    tabToFocus: number;
    siblingTabHasFocus: boolean;
    activator: ReactElement;
    selected: number;
    tabs: BaseSTabProps[];
    handleMeasurement(measurements: STabMeasurements): void;
}

export const STabMeasurer = memo(function STabMeasurer({
    selected,
    tabs,
    activator,
    tabToFocus,
    siblingTabHasFocus,
    handleMeasurement: handleMeasurementProp,
}: STabMeasurerProps) {
    const containerNode = useRef<HTMLDivElement>(null);
    const animationFrame = useRef<number | null>(null);

    const handleMeasurement = useCallback(() => {
        if (animationFrame.current) {
            cancelAnimationFrame(animationFrame.current);
        }

        animationFrame.current = requestAnimationFrame(() => {
            if (!containerNode.current) {
                return;
            }

            const containerWidth = containerNode.current.offsetWidth - 20 - 28;
            const hiddenTabNodes = containerNode.current.children;
            const hiddenTabNodesArray = Array.from(hiddenTabNodes);
            const hiddenTabWidths = hiddenTabNodesArray.map((node) => {
                const buttonWidth = Math.ceil(node.getBoundingClientRect().width);
                return buttonWidth + 4;
            });
            const disclosureWidth = hiddenTabWidths.pop() || 0;

            handleMeasurementProp({
                containerWidth,
                disclosureWidth,
                hiddenTabWidths,
            });
        });
    }, [handleMeasurementProp]);

    useEffect(() => {
        handleMeasurement();
    }, [handleMeasurement, tabs]);

    useComponentDidMount(() => {
        if (process.env.NODE_ENV === 'development') {
            setTimeout(handleMeasurement, 0);
        }
    });

    const tabsMarkup = tabs.map((tab, index) => {
        return (
            <STab
                measuring
                key={`$${tab.id}Hidden`}
                id={`${tab.id}Measurer`}
                siblingTabHasFocus={siblingTabHasFocus}
                focused={index === tabToFocus}
                selected={index === selected}
                url={tab.url}
                content={tab.content}
            />
        );
    });

    const classname = classNames(styles.Tabs, styles.TabsMeasurer);

    useEventListener('resize', handleMeasurement);

    return (
        <div className={classname} ref={containerNode}>
            {tabsMarkup}
            {activator}
        </div>
    );
});

function noop() {}
